import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import type { DotLottie } from "@lottiefiles/dotlottie-react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import clsx from "clsx";

import outroAnimation from "@/assets/lottie/outro.lottie";
import coinFallAnimation from "@/assets/lottie/coin-fall.lottie";

import DefaultContentContainer from "@/components/shared/DefaultContainer/DefaultContentContainer";
import { useCurrentRoom, usePlayersFinanceType, useStats } from "@/state/selectors";
import ScreenIcon from "@/components/icons/ScreenIcon";
import Button from "@/components/form/Button";
import ArrowRightIcon from "@/components/icons/ArrowRightIcon";
import { useShareAsImage } from "@/hooks/useShareAsImage";
import { useGameStore } from "@/state/gameStore";
import { useGameConfiguration } from "@/state/cacheStoreHooks";
import ShareContent from "@/components/screens/GameEndSequence/components/ShareContent";
import GameEndStatsDisplay from "@/components/screens/GameEndSequence/components/GameEndStatsDisplay";
import Polaroid from "@/components/screens/GameEndSequence/components/Polaroid";
import { PAGE_URL } from "@/constants/envConstants";
import { getEndScreenRoomImage } from "@/utils/roomUtils";
import GameEndMessageStep from "@/components/screens/GameEndSequence/components/GameEndMessageStep";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import { usePreloadGameEndScreenAsset } from "@/libs/assetPreloading/usePreloadGameEndScreenAsset";

type Phase = "lottie" | "message" | "content" | "polaroid" | "coins";

const contentVariants = {
    initial: { y: "120%" },
    animate: {
        y: 0,
        transition: { duration: 0.5, ease: [0.34, 1.56, 0.64, 1] },
    },
} as const;

const polaroidVariants = {
    initial: { opacity: 0, y: "-100%" },
    animate: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", duration: 0.6, bounce: 0.3 },
    },
} as const;

type Props = {};

const GameEndSequence = ({}: Props) => {
    const [phase, setPhase] = useState<Phase>("lottie");
    const [showBg, setShowBg] = useState(false);
    const lottieRef = useRef<DotLottie | null>(null);

    const stats = useStats();
    const financeType = usePlayersFinanceType();

    const gameOverReason = useGameStore((state) => state.gameOverReason);
    const gameConfiguration = useGameConfiguration();
    const room = useCurrentRoom();
    const { ref, share, prepareShare, isPreparing } = useShareAsImage();
    const resetGame = useGameStore((state) => state.resetGame);

    useEffect(() => {
        if (phase === "content") {
            prepareShare();
        }
    }, [phase]);

    const onLottieRef = useCallback((instance: DotLottie | null) => {
        if (!instance) return;
        lottieRef.current = instance;

        instance.addEventListener("play", () => {
            setTimeout(() => setShowBg(true), 1000);
        });

        setTimeout(() => {
            setPhase("message");
        }, 5000);
    }, []);

    const handleMessageComplete = useCallback(() => {
        setPhase("content");
    }, []);

    if (!room) {
        return null;
    }

    const shareText = gameConfiguration?.shareText ? gameConfiguration?.shareText + " " + PAGE_URL : PAGE_URL;

    const showContent = phase === "content" || phase === "polaroid" || phase === "coins";
    const showPolaroid = phase === "polaroid" || phase === "coins";
    const showCoins = phase === "coins";

    const roomToDisplay = getEndScreenRoomImage({ room, gameOverReason });
    usePreloadGameEndScreenAsset(roomToDisplay);
    return (
        <>
            {/* Off-screen share content — always in DOM */}
            <div className="absolute" style={{ left: "-9999px", top: "-9999px" }}>
                <ShareContent financeType={financeType} roomImage={roomToDisplay} ref={ref} stats={stats} />
            </div>

            <div className={clsx("relative size-full", "")}>
                {/* Background splash image */}
                {gameConfiguration?.SplashImage && (
                    <motion.div
                        className="absolute inset-0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: showBg ? 1 : 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <div className="absolute -inset-[20%] blur-xl">
                            <StrapiMedia
                                size="lg"
                                image={gameConfiguration.SplashImage}
                                className="size-full"
                                objectFit="fill"
                                priority
                                useImageAspectRatio={false}
                            />
                        </div>
                    </motion.div>
                )}

                {/* Layer 1: Outro lottie */}
                {(phase === "lottie" || phase === "message") && (
                    <div className="absolute inset-0">
                        <DotLottieReact
                            src={outroAnimation}
                            autoplay
                            loop={false}
                            layout={{ fit: "cover" }}
                            dotLottieRefCallback={onLottieRef}
                        />
                    </div>
                )}

                {/* Layer 2: Message modal with background lottie */}
                {phase === "message" && <GameEndMessageStep onComplete={handleMessageComplete} />}

                {/* Layer 3: Game end content */}
                {showContent && (
                    <div className="size-full flex flex-col justify-end pb-6">
                        {/* Polaroid + coins area */}
                        {showPolaroid && (
                            <motion.div
                                className="flex-grow"
                                variants={polaroidVariants}
                                initial="initial"
                                animate="animate"
                                onAnimationComplete={() => setPhase("coins")}
                            >
                                <Polaroid roomImage={roomToDisplay} />
                            </motion.div>
                        )}

                        {/* Bottom summary card */}
                        <motion.div
                            className="px-4"
                            variants={contentVariants}
                            initial="initial"
                            animate="animate"
                            onAnimationComplete={() => setPhase("polaroid")}
                        >
                            <DefaultContentContainer className="relative p-4 flex flex-col items-start justify-start gap-3 z-10">
                                <div className="absolute right-2 aspect-[300/720] w-[100px] -translate-y-full top-[44px]">
                                    {showCoins && coinFallAnimation && (
                                        <DotLottieReact
                                            src={coinFallAnimation}
                                            autoplay
                                            loop={false}
                                            layout={{ fit: "cover" }}
                                        />
                                    )}
                                </div>
                                <div className="text-2xl font-bold leading-7">Mein Leben 2045</div>
                                <GameEndStatsDisplay stats={stats} theme="inner" />
                                <DefaultContentContainer
                                    theme="inner"
                                    rounded="sm"
                                    shadow={false}
                                    className="w-full cursor-pointer space-y-2 p-3"
                                >
                                    <div className="flex items-center w-full font-bold justify-between text-white/65 text-xs leading-tight">
                                        <span className="tracking-tight">Ich bin Finanztyp</span>
                                        <ScreenIcon className="text-white/65" />
                                    </div>
                                    <div className="text-2xl font-bold leading-5 tracking-normal">
                                        {financeType?.name || "--"}
                                    </div>
                                    <div className="leading-5 tracking-tight text-white/65">
                                        {financeType?.subline || "--"}
                                    </div>
                                </DefaultContentContainer>
                                <div className="flex w-full gap-x-2">
                                    <Button
                                        className="shrink-1"
                                        content="between"
                                        disabled={isPreparing}
                                        onClick={() => share(shareText)}
                                    >
                                        <span className="text-xs">Teilen</span>
                                        <ArrowRightIcon />
                                    </Button>
                                    <Button onClick={resetGame} className="shrink-1" content="between">
                                        <span className="text-xs">Nochmal</span>
                                        <ArrowRightIcon />
                                    </Button>
                                </div>
                            </DefaultContentContainer>
                        </motion.div>
                    </div>
                )}
            </div>
        </>
    );
};

export default GameEndSequence;
